/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  partnerRoleOptions,
  partnerRoles,
  SortDirection,
  StatusOperations,
  StatusOptions,
  StatusTypes
} from 'const';
import {
  CHANGE_USER_ROLE,
  DELETE_ACCOUNT,
  GET_ALL_ACCOUNTS,
  GET_COMPANIES_LIST,
  RESEND_EMAIL,
  UPDATE_ACCOUNT
} from 'redux/sagas/actions/people';

import {
  RESET_STATE,
  SET_SEARCH_PATTERN,
  SET_SELECTED_ROLES,
  SET_SELECTED_STATUS_OPTIONS,
  SET_SORT_DATA
} from 'redux/slices/people';
import { Table } from 'ui/atoms';

import { CrudLayout, PeopleSideBar } from 'ui/components';
import AccountDetailsModal from 'ui/components/Modals/AccountDetailsModal';

import { columnDetails } from './columnDetails';
import { isInternalUser } from '../../utils/common';
import { MODAL_OPEN } from 'redux/slices/modals';
import DownloadReportModal from '../../ui/components/Modals/DownloadReportModal';

const People = ({
  getAllAccounts,
  peopleList,
  updateAccount,
  totalPages,
  totalRecords,
  isSideBarOpen,
  pageSize,
  deleteAccount,
  userRole,
  setSelectedRoles,
  searchPattern,
  setSearchPattern,
  resetToInitialState,
  isLoading,
  setSelectedStatusOptions,
  resendEmail,
  changeUserRole,
  setSortData,
  corporate = false,
  pageNo,
  sortBy,
  sortDirection,
  networkError,
  getCompaniesList,
  openDownloadReportModal
}) => {
  const { t } = useTranslation();
  const [headerColumns, setHeaderColumns] = useState(columnDetails);

  const invokeCorrectGetData = (page, size) => {
    getAllAccounts(page, size);
  };

  const onStatusChange = account => {
    const actionType =
      account.status === StatusTypes.ENABLED
        ? StatusOperations.DISABLE
        : StatusOperations.ENABLE;

    const idFromEmail = account.email.substr(0, account.email.indexOf('@'));
    updateAccount(account.cognitoId || idFromEmail, actionType);
  };

  const onDeleteAccount = cognitoId => {
    deleteAccount(cognitoId);
  };

  const canUserAddOrSelect = () =>
    userRole === partnerRoles.ADMIN ||
    userRole === partnerRoles.TECHNICAL_PARTNER_SUPER_USER ||
    userRole === partnerRoles.COMMERCIAL_PARTNER_SUPER_USER ||
    userRole === partnerRoles.SYSTEM_ADMIN;

  useEffect(() => {
    setSearchPattern('');
    invokeCorrectGetData(0, pageSize);
    setHeaderColumns([...columnDetails]);

    return resetToInitialState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  useEffect(() => {
    if (isInternalUser(userRole)) {
      getCompaniesList();
    }
    return resetToInitialState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccountsByPage = (pageNumber, pageSizeValue) => {
    invokeCorrectGetData(pageNumber, pageSizeValue);
  };

  const onSearchPatternChange = pattern => {
    setSearchPattern(pattern);
    getAllAccounts(0, pageSize);
  };

  const onDropdownChange = selected => {
    setSelectedRoles(selected?.role?.map(row => row.value));
    setSelectedStatusOptions(selected?.status?.map(row => row.value));
    invokeCorrectGetData(0, pageSize);
  };

  const onResendEmail = cognitoId => {
    resendEmail(cognitoId);
  };

  const onSortByColumn = columnName => {
    setSortData(columnName);
    invokeCorrectGetData(0, pageSize);
  };

  const onUpdateMultipleAccounts = (ids, actionType) => {
    updateAccount(ids, actionType);
  };

  useEffect(() => {
    const filteredColumn = isSideBarOpen
      ? columnDetails.filter(
          column =>
            column.accessor === 'email' ||
            column.accessor === 'role' ||
            column.accessor === 'status'
        )
      : columnDetails;
    setHeaderColumns(filteredColumn);
  }, [isSideBarOpen]);

  return (
    <CrudLayout
      title={t('people_title')}
      description={t('people_description')}
      dropdowns={[
        {
          options: partnerRoleOptions(),
          placeholder: t('role'),
          menuWidth: 250,
          isMulti: true,
          checkBoxMenu: true,
          isClearable: false,
          detachedOptions: true,
          name: 'role',
          resetSelection: true
        },
        {
          options: StatusOptions(),
          placeholder: t('status'),
          menuWidth: 250,
          isMulti: true,
          checkBoxMenu: true,
          isClearable: false,
          detachedOptions: true,
          name: 'status',
          resetSelection: true
        }
      ]}
      onSubmitDropdown={onDropdownChange}
      searchPlaceholder={t('search_description_people')}
      searchValue={searchPattern}
      onSubmitSearch={onSearchPatternChange}
      Sidebar={canUserAddOrSelect() ? PeopleSideBar : null}
      onDownloadCsv={() => openDownloadReportModal()}
    >
      <>
        <Table
          headerColumns={headerColumns}
          data={peopleList}
          onToggle={onStatusChange}
          selection={!isSideBarOpen && canUserAddOrSelect()}
          pagination
          totalPages={totalPages}
          totalRecords={totalRecords}
          pageNo={pageNo}
          pageSize={pageSize}
          onPageChangeFetch={fetchAccountsByPage}
          onDelete={onDeleteAccount}
          onDropdownChange={onDropdownChange}
          dropDownOptions={partnerRoleOptions()}
          onResendEmail={onResendEmail}
          onUpdateAccountStatus={onUpdateMultipleAccounts}
          onChangeUserRole={changeUserRole}
          onSortByColumn={onSortByColumn}
          corporate={corporate}
          isPeopleList
          isLoading={isLoading}
          itemPropForSelection="email"
          sortingInfo={{
            sortingDirection: sortDirection || SortDirection.ASC,
            sortingOn: sortBy
          }}
          customPlaceholder={
            networkError
              ? 'Error, system is not able to load data, try again'
              : null
          }
        />
        <AccountDetailsModal />
        <DownloadReportModal />
      </>
    </CrudLayout>
  );
};

People.propTypes = {
  getAllAccounts: PropTypes.func,
  peopleList: PropTypes.array,
  updateAccount: PropTypes.func,
  totalPages: PropTypes.number,
  totalRecords: PropTypes.number,
  isSideBarOpen: PropTypes.bool,
  pageSize: PropTypes.number,
  deleteAccount: PropTypes.func,
  userRole: PropTypes.string,
  setSelectedRoles: PropTypes.func,
  searchPattern: PropTypes.string,
  setSearchPattern: PropTypes.func,
  resetToInitialState: PropTypes.func,
  isLoading: PropTypes.bool,
  setSelectedStatusOptions: PropTypes.func,
  resendEmail: PropTypes.func,
  changeUserRole: PropTypes.func,
  setSortData: PropTypes.func,
  corporate: PropTypes.bool,
  pageNo: PropTypes.number,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  networkError: PropTypes.bool,
  getCompaniesList: PropTypes.func,
  openDownloadReportModal: PropTypes.func
};

export default connect(
  state => ({
    peopleList: state?.people?.peopleList || [],
    totalPages: state?.people?.totalPagesAvailable || 0,
    totalRecords: state?.people?.totalRecords || 0,
    isSideBarOpen: state?.sidebar?.sidebarOpen,
    pageNo: state?.people?.pageNo,
    pageSize: state.people?.pageSize,
    userRole: state?.authentication?.role,
    searchPattern: state?.people?.searchPattern,
    isLoading: state?.people?.isLoading,
    selectedStatusOptions: state?.people?.selectedStatusOptions,
    isMenuOpen: state?.people?.isMenuOpen,
    sortBy: state?.people?.sortBy,
    sortDirection: state?.people?.sortDirection,
    networkError: state.people?.networkError
  }),
  dispatch => ({
    getAllAccounts: (pageNo, pageSize, roles) =>
      dispatch({
        type: GET_ALL_ACCOUNTS._REQUEST,
        payload: { pageNo, pageSize, roles }
      }),
    updateAccount: (ids, targetStatus) =>
      dispatch({
        type: UPDATE_ACCOUNT._REQUEST,
        payload: { cognitoIds: ids, targetStatus }
      }),
    deleteAccount: cognitoIds =>
      dispatch({ type: DELETE_ACCOUNT._REQUEST, payload: { cognitoIds } }),
    setSelectedRoles: roles => dispatch(SET_SELECTED_ROLES(roles)),
    setSearchPattern: searchPattern =>
      dispatch(SET_SEARCH_PATTERN(searchPattern)),
    resetToInitialState: () => dispatch(RESET_STATE()),
    setSelectedStatusOptions: status =>
      dispatch(SET_SELECTED_STATUS_OPTIONS(status)),
    resendEmail: cognitoId =>
      dispatch({ type: RESEND_EMAIL._REQUEST, payload: cognitoId }),
    changeUserRole: (ids, role_type, selected_row) =>
      dispatch({
        type: CHANGE_USER_ROLE._REQUEST,
        payload: {
          cognitoId: ids,
          targetRole: role_type,
          selectedRow: selected_row
        }
      }),
    setSortData: sortBy =>
      dispatch(
        SET_SORT_DATA({
          sortBy
        })
      ),
    getCompaniesList: () => dispatch({ type: GET_COMPANIES_LIST._REQUEST }),
    openDownloadReportModal: () =>
      dispatch(MODAL_OPEN({ id: 'DownloadReportModal' }))
  })
)(People);
